import { faCheck, faTimes, faEye } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';

import Avatar from '@/components/Avatar';
import Badge from '@/components/Badge';
import Button from '@/components/Button';
import IconicButton from '@/components/IconicButton';
import Stack from '@/components/Stack';
import TextStack from '@/components/TextStack';
import { Body, Small } from '@/components/Typography';
import { TableRow, TableCell } from '@/elements/Table';
import useMediaQuery from '@/hooks/useMediaQuery';
import { formatDateSimple } from '@/util/datetime';
import ImagePreviewModal from '../shared/ImagePreviewModal';
import RejectModal from '../shared/RejectModal';
import { WorkerUniform } from './api';

type Props = {
  uniform: WorkerUniform;
  onAccept: (id: number, comment?: string) => Promise<void>;
  onReject: (id: number, comment: string) => Promise<void>;
  isAccepting?: boolean;
  isRejecting?: boolean;
  onActionComplete?: () => void;
};

const UniformRowItem = ({
  uniform,
  onAccept,
  onReject,
  isAccepting = false,
  isRejecting = false,
  onActionComplete,
}: Props) => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [showImageModal, setShowImageModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const isPending = uniform.status === 'pending';
  const workerName = uniform.worker
    ? `${uniform.worker.user.first_name} ${uniform.worker.user.last_name}`
    : 'Unknown Worker';
  const workerEmail = uniform.worker?.user.email || '';
  // Get skill name from skill_request_v2 array (can have multiple, show first one)
  const primarySkillName =
    uniform.skill_request_v2?.[0]?.skill?.display_name_en ||
    uniform.skill_request_v2?.[0]?.skill?.name ||
    'N/A';

  const handleAccept = async () => {
    try {
      await onAccept(uniform.id);
      onActionComplete?.();
    } catch (error) {
      console.error('Failed to accept uniform:', error);
    }
  };

  const handleReject = async (comment: string) => {
    try {
      await onReject(uniform.id, comment);
      setShowRejectModal(false);
      onActionComplete?.();
    } catch (error) {
      console.error('Failed to reject uniform:', error);
      throw error;
    }
  };

  const getStatusBadge = () => {
    switch (uniform.status) {
      case 'accepted':
        return <Badge label="Accepted" status="theme" size="sm" />;
      case 'rejected':
        return <Badge label="Rejected" status="danger" size="sm" />;
      default:
        return <Badge label="Pending" status="warning" size="sm" />;
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Stack gap={12}>
            <Avatar
              size="sm"
              src={undefined} // avatarUrl not in API response
              firstName={uniform.worker?.user.first_name}
            />
            <TextStack spacing="tight">
              <Body weight="medium">{workerName}</Body>
              <Small>{workerEmail}</Small>
            </TextStack>
          </Stack>
        </TableCell>
        <TableCell>
          {uniform.image_url ? (
            <Stack gap={8} align="center">
              <img
                src={uniform.image_url}
                alt="Uniform preview"
                style={{
                  width: '60px',
                  height: '60px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowImageModal(true);
                }}
              />
              <IconicButton
                a11yLabel="View full image"
                appearance="plain"
                icon={faEye}
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowImageModal(true);
                }}
              />
            </Stack>
          ) : (
            <Small color="light">No image</Small>
          )}
        </TableCell>
        <TableCell>
          <Body>{primarySkillName}</Body>
          {uniform.skill_request_v2 && uniform.skill_request_v2.length > 1 && (
            <Small color="light">
              +{uniform.skill_request_v2.length - 1} more
            </Small>
          )}
        </TableCell>
        <TableCell>
          <Small>{formatDateSimple(uniform.created_at)}</Small>
        </TableCell>
        <TableCell>{getStatusBadge()}</TableCell>
        <TableCell>
          {isPending ? (
            <Stack gap={8} justify="end">
              <Button
                a11yLabel="Accept uniform"
                appearance="secondary"
                iconLeft={faCheck}
                isLoading={isAccepting}
                label={phoneOnly ? '' : 'Accept'}
                onClick={handleAccept}
              />
              <Button
                a11yLabel="Reject uniform"
                appearance="secondary"
                iconLeft={faTimes}
                isLoading={isRejecting}
                label={phoneOnly ? '' : 'Reject'}
                onClick={() => setShowRejectModal(true)}
                status="danger"
              />
            </Stack>
          ) : (
            <Small color="light">Processed</Small>
          )}
        </TableCell>
      </TableRow>
      <ImagePreviewModal
        isOpen={showImageModal}
        imageUrl={uniform.image_url}
        onClose={() => setShowImageModal(false)}
        title={`Uniform - ${workerName}`}
      />
      {showRejectModal && (
        <RejectModal
          isOpen={showRejectModal}
          isLoading={isRejecting}
          onClose={() => setShowRejectModal(false)}
          onReject={handleReject}
          requireComment={true}
          title="Reject Uniform"
        />
      )}
    </>
  );
};

export default UniformRowItem;
